import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocationFixed, setIsLocationFixed] = useState(false);

  useEffect(() => {
    loadLocations();
    
    const savedSelectedLocation = localStorage.getItem('laVieSaigonSelectedLocation');
    const savedLocationFixed = localStorage.getItem('laVieSaigonLocationFixed');
    
    if (savedLocationFixed) {
      setIsLocationFixed(JSON.parse(savedLocationFixed));
    }
    
    if (savedSelectedLocation) {
      setSelectedLocation(JSON.parse(savedSelectedLocation));
    }
  }, []);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      const formattedLocations = (data || []).map(location => ({
        ...location,
        workingHours: location.working_hours || {},
        settings: location.settings || {}
      }));
      
      setLocations(formattedLocations);
      
      // Set first location as selected if none is selected
      if (formattedLocations.length > 0 && !selectedLocation) {
        setSelectedLocation(formattedLocations[0]);
        localStorage.setItem('laVieSaigonSelectedLocation', JSON.stringify(formattedLocations[0]));
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const addLocation = async (location) => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .insert([{
          name: location.name,
          address: location.address,
          phone: location.phone,
          is_active: location.isActive,
          working_hours: location.workingHours,
          settings: location.settings
        }])
        .select()
        .single();

      if (error) throw error;
      
      await loadLocations();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to add location' };
    }
  };

  const updateLocation = async (locationId, updatedLocation) => {
    try {
      const { error } = await supabase
        .from('locations')
        .update({
          name: updatedLocation.name,
          address: updatedLocation.address,
          phone: updatedLocation.phone,
          is_active: updatedLocation.isActive,
          working_hours: updatedLocation.workingHours,
          settings: updatedLocation.settings,
          updated_at: new Date().toISOString()
        })
        .eq('id', locationId);

      if (error) throw error;
      
      await loadLocations();
      
      // Update selected location if it's the one being updated
      if (selectedLocation?.id === locationId) {
        const updatedSelectedLocation = { ...selectedLocation, ...updatedLocation };
        setSelectedLocation(updatedSelectedLocation);
        localStorage.setItem('laVieSaigonSelectedLocation', JSON.stringify(updatedSelectedLocation));
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update location' };
    }
  };

  const deleteLocation = async (locationId) => {
    try {
      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;
      
      await loadLocations();
      
      // If deleted location was selected, select first available location
      if (selectedLocation?.id === locationId) {
        const remainingLocations = locations.filter(loc => loc.id !== locationId);
        const newSelected = remainingLocations.length > 0 ? remainingLocations[0] : null;
        setSelectedLocation(newSelected);
        if (newSelected) {
          localStorage.setItem('laVieSaigonSelectedLocation', JSON.stringify(newSelected));
        } else {
          localStorage.removeItem('laVieSaigonSelectedLocation');
        }
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete location' };
    }
  };

  const selectLocation = (location) => {
    if (!isLocationFixed) {
      setSelectedLocation(location);
      localStorage.setItem('laVieSaigonSelectedLocation', JSON.stringify(location));
    }
  };

  const setLocationFixed = (fixed) => {
    setIsLocationFixed(fixed);
    localStorage.setItem('laVieSaigonLocationFixed', JSON.stringify(fixed));
  };

  const getActiveLocations = () => {
    return locations.filter(location => location.is_active || location.isActive);
  };

  return (
    <LocationContext.Provider value={{
      locations,
      selectedLocation,
      isLocationFixed,
      addLocation,
      updateLocation,
      deleteLocation,
      selectLocation,
      setLocationFixed,
      getActiveLocations
    }}>
      {children}
    </LocationContext.Provider>
  );
};